/**
 * The Planned Pod panel — Resource Plan Epic 2 (RP-2.1 … RP-2.4), M3.
 *
 * The plan's intended delivery team, and how far it has drifted from the Shape it started
 * as.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * SHAPES ARE MEASURED, NEVER ENFORCED (non-negotiable 7)
 * ──────────────────────────────────────────────────────────────────────────────
 * There is no warning styling anywhere on this panel for a Custom or Modified Pod. The
 * origin line is a NEUTRAL sentence, the nearest-Shape line is a neutral offer, and
 * neither carries an icon, a color or a severity. If a planner ever feels nagged for
 * building a Custom team, they will pick the nearest Shape and lie by omission, and M7's
 * Shape Adherence report is then worthless. The only colored states on this panel are
 * RP-2.4's two reconciliation flags, and both are informational amber rather than error
 * red — a plan reaches Won with either.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * POINTS ARE A REFERENCE FIGURE
 * ──────────────────────────────────────────────────────────────────────────────
 * RP-2.1: "Default points per Position are shown as a reference figure only — they are
 * not written anywhere." They arrive as strings and are rendered muted, alongside the FTE
 * equivalent, under a heading that says so. Nothing on this panel adds them up, and
 * nothing writes them (non-negotiable 1, the hours-to-points firewall).
 */

import { Info, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDisciplines } from "@/hooks/use-disciplines";
import {
  useClearPlannedPod,
  useComposePlannedPod,
  useCreatePlannedPodPosition,
  useDeletePlannedPodPosition,
  usePlanShapes,
  useSetPlanLinePosition,
  useUpdatePlannedPodPosition,
} from "@/hooks/use-plan-composition";
import { canEditPlanContent } from "@/lib/plan-version";
import { cn } from "@/lib/utils";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

const POSITION_CODES = ["R1", "R2", "R3", "R4"] as const;

function replaceWarning(linked: number): string {
  return `This replaces the Planned Pod. ${linked} Plan Line${linked === 1 ? "" : "s"} currently covered by a Position will become uncovered — their hours are kept, but you will need to link them again. Continue?`;
}

function clearWarning(linked: number): string {
  return `This clears the Planned Pod. ${linked} Plan Line${linked === 1 ? "" : "s"} currently covered by a Position will become uncovered — their hours are kept, but you will need to link them again. Continue?`;
}

export function PlannedPodPanel({ plan }: { plan: ResourcePlanDetailView }) {
  const { data: shapes } = usePlanShapes(plan.id);
  const { data: disciplines } = useDisciplines();
  const compose = useComposePlannedPod(plan.id);
  const clear = useClearPlannedPod(plan.id);
  const addPosition = useCreatePlannedPodPosition(plan.id);
  const updatePosition = useUpdatePlannedPodPosition();
  const removePosition = useDeletePlannedPodPosition();
  const linkLine = useSetPlanLinePosition();

  const [adding, setAdding] = useState(false);
  const pod = plan.plannedPod ?? null;
  const canPodEdit = canEditPlanContent(plan);
  const busy =
    compose.isPending ||
    clear.isPending ||
    addPosition.isPending ||
    updatePosition.isPending ||
    removePosition.isPending;

  // ── No Pod composed yet. A perfectly valid state — Epic 2 is a planning aid. ──
  if (pod === null) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 shadow-xs">
        <h3 className="font-display text-base font-semibold">Planned Pod</h3>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Optional — but composing one makes Handover pre-fill the Pod instead of the RM retyping
          it.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {(shapes ?? []).map((shape) => (
            <Button
              key={shape.id}
              variant={shape.isTierDefault ? "default" : "outline"}
              size="sm"
              disabled={!canEditPlanContent(plan) || busy}
              onClick={() => compose.mutate({ shapeId: shape.id })}
              title={shape.disciplineCodes.join(" · ")}
            >
              {shape.name}
              {shape.isTierDefault && (
                <span className="ml-1.5 text-xs opacity-80">tier default</span>
              )}
            </Button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            disabled={!canEditPlanContent(plan) || busy}
            onClick={() => compose.mutate({ shapeId: null })}
          >
            Custom
          </Button>
        </div>
        {(shapes ?? []).length > 0 && (
          <p className="mt-2 text-sm text-muted-foreground">
            Selecting a Shape adds a plan-level Plan Line per Position at the Rate Card rate.
          </p>
        )}
      </div>
    );
  }

  const disciplineOptions = (disciplines ?? []).filter((d) => d.active);
  // How much a replace or a clear would cost. Plan Lines are never deleted — the FK is
  // `set null` — but they lose the Position that covered them, and re-linking a sized
  // grid by hand is real work.
  const linkedLineCount = plan.lines.filter(
    (l) => l.plannedPodPositionId !== null && l.plannedPodPositionId !== undefined,
  ).length;
  const usedCodes = new Set(pod.positions.map((p) => p.positionCode));
  const freeCodes = POSITION_CODES.filter((c) => !usedCodes.has(c));
  const uncovered = plan.lines.filter((l) => pod.uncoveredLineIds.includes(l.id));

  return (
    <div className="space-y-4">
      {/* ── Origin ── */}
      <div className="rounded-lg border border-border bg-card p-4 shadow-xs">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-display text-base font-semibold">Planned Pod</h3>
              {/* Neutral, on purpose. Template / Modified / Custom are three facts, not
                  three grades. */}
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                {pod.shapeSource}
              </span>
            </div>
            {/* RP-2.2: "origin and deviations on one line". */}
            <p className="mt-1 text-sm text-foreground">{pod.originSummary}</p>
            {pod.nearestShape !== null && pod.shapeSource !== "Template" && (
              <p className="mt-1 text-sm text-muted-foreground">{pod.nearestShape.summary}</p>
            )}
          </div>

          <div className="flex shrink-0 flex-wrap gap-2">
            {pod.nearestShape !== null &&
              pod.nearestShape.distance > 0 &&
              canEditPlanContent(plan) && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    const shapeId = pod.nearestShape?.shapeId ?? null;
                    if (shapeId === null) return;
                    /*
                     * Re-composing REPLACES the Pod: every Position is deleted, so every
                     * Plan Line linked to one silently unlinks (the FK is `set null`). On a
                     * plan with a sized grid that is a dozen lines dropping into "not
                     * covered by the Planned Pod" from one click on what reads as a
                     * neutral suggestion. Confirm, and say what it costs.
                     */
                    if (linkedLineCount > 0 && !window.confirm(replaceWarning(linkedLineCount))) {
                      return;
                    }
                    // No new Plan Lines on a RE-compose: the plan already has its grid, and
                    // adding a plan-level line per Position on top would duplicate work the
                    // planner has already priced.
                    compose.mutate({ shapeId, createLines: false });
                  }}
                >
                  Use {pod.nearestShape.shapeName}
                </Button>
              )}
            {canEditPlanContent(plan) && (
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => {
                  if (linkedLineCount > 0 && !window.confirm(clearWarning(linkedLineCount))) {
                    return;
                  }
                  clear.mutate();
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ── Positions ── */}
      <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-xs">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/50 text-left">
            <tr className="border-b border-border">
              <th className="px-3 py-2 font-medium text-muted-foreground">Position</th>
              <th className="px-3 py-2 font-medium text-muted-foreground">Expected Discipline</th>
              <th className="px-3 py-2 font-medium text-muted-foreground">Labor Category</th>
              <th className="px-3 py-2 text-right font-medium text-muted-foreground">
                Planned hours
              </th>
              <th
                className="px-3 py-2 text-right font-medium text-muted-foreground"
                title="Reference figures only — never written anywhere."
              >
                FTE · points
              </th>
              <th className="w-10 px-3 py-2" />
            </tr>
          </thead>
          <tbody className="bg-background">
            {pod.positions.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                  No Positions yet. Add the ones this engagement needs.
                </td>
              </tr>
            )}
            {pod.positions.map((position) => (
              <tr key={position.id} className="border-b border-border last:border-0">
                <td className="px-3 py-2 font-mono font-medium">{position.positionCode}</td>
                <td className="px-3 py-2">
                  {canPodEdit ? (
                    <Select
                      value={position.expectedDisciplineId}
                      onValueChange={(id) =>
                        updatePosition.mutate({
                          positionId: position.id,
                          expectedDisciplineId: id,
                        })
                      }
                    >
                      <SelectTrigger className="h-7 w-36 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {disciplineOptions.map((d) => (
                          <SelectItem key={d.id} value={d.id} className="text-sm">
                            {d.code} — {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span>{position.expectedDisciplineCode}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {position.laborCategoryName ?? (
                    <span
                      className="text-muted-foreground"
                      title="No priced Labor Category on the Rate Card — attach one on a Plan Line to price it."
                    >
                      none priced
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono">{position.totalHours}</td>
                <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                  {position.fte}
                  {position.defaultPoints !== null && ` · ${position.defaultPoints}`}
                </td>
                <td className="px-3 py-2">
                  {canPodEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-error-700"
                      disabled={busy}
                      onClick={() => removePosition.mutate(position.id)}
                      aria-label={`Remove ${position.positionCode}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-muted/30">
            <tr>
              <td colSpan={6} className="px-3 py-2">
                {canPodEdit && freeCodes.length > 0 && !adding && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-sm"
                    onClick={() => setAdding(true)}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Add Position
                  </Button>
                )}
                {adding && (
                  <AddPositionRow
                    freeCodes={freeCodes}
                    disciplines={disciplineOptions}
                    pending={addPosition.isPending}
                    onCancel={() => setAdding(false)}
                    onAdd={(input) =>
                      addPosition.mutate(input, { onSuccess: () => setAdding(false) })
                    }
                  />
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ── RP-2.4's two reconciliation flags. Informational, never blocking. ── */}
      {(pod.positions.some((p) => !p.hasPlannedWork) || uncovered.length > 0) && (
        <div className="rounded-lg border border-border bg-card p-4 shadow-xs">
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <div className="min-w-0 space-y-3 text-sm">
              <p className="text-muted-foreground">
                Neither of these blocks anything — a plan reaches Won with either.
              </p>

              {pod.positions.some((p) => !p.hasPlannedWork) && (
                <div>
                  <p className="font-medium">Positions with no planned work</p>
                  <p className="mt-0.5 text-muted-foreground">
                    {pod.positions
                      .filter((p) => !p.hasPlannedWork)
                      .map((p) => `${p.positionCode} (${p.expectedDisciplineCode})`)
                      .join(", ")}
                  </p>
                </div>
              )}

              {uncovered.length > 0 && (
                <div>
                  <p className="font-medium">Plan Lines not covered by the Planned Pod</p>
                  <ul className="mt-1 space-y-1">
                    {uncovered.map((line) => (
                      <li key={line.id} className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate">
                          {line.clientLaborCategory ?? line.laborCategoryName}
                          <span className="ml-2 font-mono text-muted-foreground">
                            {line.totalHours} h
                          </span>
                        </span>
                        {canPodEdit && pod.positions.length > 0 && (
                          <Select
                            value=""
                            onValueChange={(positionId) =>
                              linkLine.mutate({ lineId: line.id, plannedPodPositionId: positionId })
                            }
                          >
                            <SelectTrigger className="h-7 w-40 text-sm">
                              <SelectValue placeholder="Cover with…" />
                            </SelectTrigger>
                            <SelectContent>
                              {pod.positions.map((p) => (
                                <SelectItem key={p.id} value={p.id} className="text-sm">
                                  {p.positionCode} — {p.expectedDisciplineCode}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddPositionRow({
  freeCodes,
  disciplines,
  pending,
  onCancel,
  onAdd,
}: {
  freeCodes: readonly string[];
  disciplines: ReadonlyArray<{ id: string; code: string; name: string }>;
  pending: boolean;
  onCancel: () => void;
  onAdd: (input: { positionCode: string; expectedDisciplineId: string }) => void;
}) {
  const [code, setCode] = useState(freeCodes[0] ?? "R1");
  const [disciplineId, setDisciplineId] = useState(disciplines[0]?.id ?? "");

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={code} onValueChange={setCode}>
        <SelectTrigger className={cn("h-7 w-20 text-sm")}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {freeCodes.map((c) => (
            <SelectItem key={c} value={c} className="text-sm">
              {c}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={disciplineId} onValueChange={setDisciplineId}>
        <SelectTrigger className="h-7 w-44 text-sm">
          <SelectValue placeholder="Discipline" />
        </SelectTrigger>
        <SelectContent>
          {disciplines.map((d) => (
            <SelectItem key={d.id} value={d.id} className="text-sm">
              {d.code} — {d.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        size="sm"
        className="h-7 px-2 text-sm"
        disabled={pending || disciplineId === ""}
        onClick={() => onAdd({ positionCode: code, expectedDisciplineId: disciplineId })}
      >
        Add
      </Button>
      <Button variant="ghost" size="sm" className="h-7 px-2 text-sm" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}
